// this hook prevents system columns from being selected
import { useCallback } from 'react';

import { isServiceColumn } from '@/components/AgGrid/helpers';

const useRangeSelectionChanged = (gridApi) => {
  /**
   * by default, if a cell is selected
   * and the grid loses focus,
   * the cell gets highlighted - undesired effect (see DA-4597)
   *
   * @summary deselects cell ranges on grid blur
   */
  const onSelectionBlur = useCallback(
    (event) => {
      const { relatedTarget } = event;
      const api = gridApi.current;
      // the focused cell also blurs when the grid is torn down on leaving the workspace
      if (api && !api.isDestroyed() && (!relatedTarget || relatedTarget.type === 'checkbox')) {
        api.clearCellSelection();
      }
    },
    [gridApi],
  );

  return useCallback(
    (event) => {
      // a queued event can arrive after the grid is destroyed (see AmountSumStatusPanelRenderer)
      if (event.api.isDestroyed()) return;

      const target = document.activeElement;
      if (target) {
        target.addEventListener('blur', onSelectionBlur, { once: true });
      }

      // service columns (the app's and AG Grid's selection column) are dropped from ranges
      const cellRanges = event.api.getCellRanges();

      if (!cellRanges || cellRanges.length === 0) return;
      const excludedColumn = cellRanges
        .map((r) => r.columns.find((el) => isServiceColumn(el.getColId())))
        .filter((r) => r !== undefined).length;

      if (!excludedColumn) return;

      const rangesParams = cellRanges.map(({ startRow, endRow, columns }) => ({
        rowStartIndex: startRow.rowIndex,
        rowStartPinned: startRow.rowPinned,
        rowEndIndex: endRow.rowIndex,
        rowEndPinned: endRow.rowPinned,
        columns: columns.map((el) => el.getColId()).filter((el) => !isServiceColumn(el)),
      }));

      event.api.clearCellSelection();
      rangesParams.forEach((params) => {
        if (params.columns.length) {
          event.api.addCellRange(params);
        }
      });
    },
    [onSelectionBlur],
  );
};

export default useRangeSelectionChanged;
